import {
  Alert,
  AlertVariants,
  Button,
  ButtonVariants,
  type CareOrder,
  Dialog,
  DialogFooterWrapper,
  Divider,
  Flex,
  HStack,
  Text,
  Tooltip,
  useCallbackRef,
  useConstant,
  type UseDisclosureWithContextReturn,
} from '@talos/kyoko';
import { useState } from 'react';
import { ACTION_METADATA, type CareOrderParser, type ImportedCareOrder } from '../types';
import { ImportedCareOrderBlotter } from './ImportedCareOrderBlotter';
import { ResultingCareOrderBlotter } from './ResultingCareOrderBlotter';
import { SimilarCareOrdersBlotter } from './SimilarCareOrdersBlotter';

type ImportActionDialogProps = UseDisclosureWithContextReturn<ImportedCareOrder> & {
  context: ImportedCareOrder; // force this to be defined (its maybe undefined in UseDisclosureWithContextReturn)
  /**
   * Called when the user decides what the action for the row should be. Is called with a new object representing the entire row,
   * with an updated action property within.
   */
  onRowActionChange: (updatedRow: ImportedCareOrder) => void;
  parser: CareOrderParser;
};

export const ImportActionDialog = ({
  context: importedCareOrder,
  onRowActionChange,
  parser,
  ...dialogProps
}: ImportActionDialogProps) => {
  const [selectedCareOrder, setSelectedCareOrder] = useState<CareOrder | undefined>(() => {
    if (importedCareOrder.action.type === 'modify') {
      return importedCareOrder.action.careOrderToModify;
    }

    return undefined;
  });
  const initialSelectedCareOrder = useConstant(selectedCareOrder);

  const handleCreateNewClick = useCallbackRef(() => {
    onRowActionChange({
      ...importedCareOrder,
      action: {
        canCreateNewCareOrder: importedCareOrder.action.canCreateNewCareOrder,
        similarCareOrders: importedCareOrder.action.similarCareOrders,
        type: 'create',
      },
    });
    dialogProps.close();
  });

  const handleModifyClick = useCallbackRef(() => {
    if (!selectedCareOrder) {
      return; // shouldnt happen
    }

    // we're modifying
    onRowActionChange({
      ...importedCareOrder,
      action: {
        canCreateNewCareOrder: importedCareOrder.action.canCreateNewCareOrder,
        similarCareOrders: importedCareOrder.action.similarCareOrders,
        type: 'modify',
        careOrderToModify: selectedCareOrder,
      },
    });
    dialogProps.close();
  });

  const determinedActionType = importedCareOrder.action.type;
  const disableCreateNew = !importedCareOrder.action.canCreateNewCareOrder;

  // We want several action buttons so we hook this up ourselves
  const customActionButtons = (
    <DialogFooterWrapper>
      <HStack justifyContent="space-between" w="100%">
        <Button
          onClick={() => dialogProps.close()}
          variant={ButtonVariants.Default}
          width="140px"
          data-testid="action-dialog-close-button"
        >
          Close
        </Button>
        <HStack gap="spacing12">
          <Text color="colorTextSubtle">Select new action: </Text>
          <Button
            onClick={handleModifyClick}
            variant={ButtonVariants.Primary}
            disabled={selectedCareOrder == null}
            data-testid="action-dialog-modify-button"
          >
            Modify selected Care Order
          </Button>
          <Tooltip
            tooltip={
              disableCreateNew
                ? 'The imported Care Order has a unique property collision with an existing valid Care Order.'
                : undefined
            }
          >
            <Button
              onClick={handleCreateNewClick}
              variant={ButtonVariants.Primary}
              data-testid="action-dialog-create-button"
              disabled={disableCreateNew}
            >
              Create new Care Order
            </Button>
          </Tooltip>
        </HStack>
      </HStack>
    </DialogFooterWrapper>
  );

  return (
    <Dialog
      {...dialogProps}
      minWidth="960px"
      width="90vw"
      maxWidth="1200px"
      closeOnClickOutside={false}
      showClose // refers to the X button top-right in the header
      closeOnEscape={false}
      title="Determine Import Action"
      customActions={customActionButtons}
    >
      <Flex flexDirection="column" h="550px" gap="spacing12" data-testid="action-dialog-content">
        <Alert dismissable={false} variant={AlertVariants.Info} flex="0">
          {ACTION_METADATA[determinedActionType].actionDialogInfo}
        </Alert>

        <Flex flexDirection="column" data-testid="action-dialog-importing-care-order-blotter">
          <Text color="colorTextDefault" mb="spacing4" alignSelf="flex-start">
            Importing Care Order
          </Text>
          <ImportedCareOrderBlotter importedCareOrder={importedCareOrder} parser={parser} />
        </Flex>

        <Divider />

        <Flex flexDirection="column" flex="1" data-testid="action-dialog-similar-care-orders-blotter">
          <Text color="colorTextDefault" mb="spacing4" alignSelf="flex-start">
            Similar existing Care Orders
          </Text>

          <SimilarCareOrdersBlotter
            importedCareOrder={importedCareOrder}
            initialSelectedCareOrder={initialSelectedCareOrder}
            onSelectionChange={setSelectedCareOrder}
            parser={parser}
          />
        </Flex>

        {selectedCareOrder && (
          <>
            <Divider />
            <Flex flexDirection="column" data-testid="action-dialog-resulting-care-order-blotter">
              <Text color="colorTextDefault" mb="spacing4" alignSelf="flex-start">
                Resulting Care Order
              </Text>
              <ResultingCareOrderBlotter
                selectedCareOrder={selectedCareOrder}
                importedCareOrder={importedCareOrder}
                parser={parser}
              />
            </Flex>
          </>
        )}
      </Flex>
    </Dialog>
  );
};
